import { useEffect } from 'react';
import { PRODUCT_CATEGORY } from '../constants/rentDetailsConfig';

// IDs for Arcade and Game Room product categories
const ARCADE_PRODUCT_CATEGORY_ID = PRODUCT_CATEGORY.ARCADE.id; // 50
const GAME_ROOM_PRODUCT_CATEGORY_ID = PRODUCT_CATEGORY.GAME.id; // 36

/**
 * Checks if a category ID matches Arcade or Game Room
 * @param {number|string} categoryId - The product category ID
 * @returns {boolean} - True if the ID matches Arcade (50) or Game Room (36)
 */
const isArcadeOrGameRoomId = (categoryId) => {
  if (!categoryId) return false;
  const idAsNumber = Number(categoryId);
  return idAsNumber === ARCADE_PRODUCT_CATEGORY_ID || idAsNumber === GAME_ROOM_PRODUCT_CATEGORY_ID;
};

/**
 * Extracts category ID from different input formats
 * @param {number|string|Object} input - Can be a direct ID, or an object with 'value' or 'id' property
 * @returns {number|string|undefined} - The extracted category ID
 */
const extractCategoryId = (input) => {
  if (!input) return undefined;
  if (typeof input === 'object' && input !== null) {
    return input.value || input.id;
  }
  return input;
};

/**
 * Extracts and trims name from sublocation data (handles different property names)
 * @param {Object} data - Object containing sublocation data
 * @returns {string} - The trimmed name
 */
const extractName = (data) => {
  const name = data?.subLocationName || data?.name || '';
  return typeof name === 'string' ? name.trim() : name;
};

/**
 * Checks if a product type is Arcade or GameRoom
 * @param {number|string|Object|Array} productTypeIdOrCategories - The product category ID, productType object with value property, or array of product category objects
 * @returns {boolean} - True if the product type is Arcade or GameRoom, false otherwise
 */
export const isArcadeOrGameRoomType = (productTypeIdOrCategories) => {
  if (!productTypeIdOrCategories) return false;

  // Handle array of product categories (e.g., from sublocationInfo.productCategories)
  if (Array.isArray(productTypeIdOrCategories)) {
    return productTypeIdOrCategories.some((category) => {
      const categoryId = category?.id;
      return isArcadeOrGameRoomId(categoryId);
    });
  }

  // Handle productType object with value property or direct ID
  const productCategoryId = extractCategoryId(productTypeIdOrCategories);
  return isArcadeOrGameRoomId(productCategoryId);
};

/**
 * Core function to calculate postingLocation value based on product type and name
 * @param {number|string|Object|undefined} productTypeIdOrObject - The product category ID or productType object
 * @param {string} name - The sublocation name
 * @param {Array} productCategoriesData - Array of product category objects
 * @returns {string} - The calculated postingLocation value
 */
const calculatePostingLocationFromTypeAndName = (productTypeIdOrObject, name, productCategoriesData) => {
  if (!productCategoriesData) return name || '';

  const isArcadeOrGameRoom = isArcadeOrGameRoomType(productTypeIdOrObject, productCategoriesData);
  // For non-Arcade/GameRoom, default to name (will be auto-populated)
  // For Arcade/GameRoom, leave empty so user can enter custom value
  return isArcadeOrGameRoom ? '' : name || '';
};

/**
 * Custom hook to sync postingLocation with Sub Location Name for non-Arcade/GameRoom product types
 * @param {Object} formik - Formik instance with values and setFieldValue
 * @param {Array} productCategoriesData - Array of product category objects
 * @returns {void}
 */
export const usePostingLocationSync = (formik, productCategoriesData) => {
  useEffect(() => {
    if (!formik || !formik.values || !productCategoriesData) return;

    const { productType } = formik.values || {};
    const name = extractName(formik.values);
    const expectedPostingLocation = calculatePostingLocationFromTypeAndName(productType, name, productCategoriesData);

    // Guard: prevent unnecessary updates if values are already equal
    if (expectedPostingLocation === formik.values.postingLocation) return;

    // For non-Arcade/GameRoom types, always sync postingLocation to name
    if (expectedPostingLocation && formik.values.postingLocation !== expectedPostingLocation) {
      formik.setFieldValue('postingLocation', expectedPostingLocation);
    }
  }, [
    formik?.values?.name,
    formik?.values?.productType,
    formik?.values?.postingLocation,
    formik?.setFieldValue,
    productCategoriesData,
  ]);
};

/**
 * Helper function to get the correct postingLocation value for submission
 * For non-Arcade/GameRoom product types, postingLocation should always equal Sub Location Name
 * @param {Object} data - Object containing productType, name, and postingLocation
 * @param {Array} productCategoriesData - Array of product category objects
 * @returns {string} - The correct postingLocation value
 */
export const getPostingLocationForSubmission = (data, productCategoriesData) => {
  if (!data || !productCategoriesData) return data?.postingLocation || data?.name || '';

  const { productType } = data || {};
  const name = extractName(data);
  const calculatedValue = calculatePostingLocationFromTypeAndName(productType, name, productCategoriesData);

  // For non-Arcade/GameRoom types, always use the calculated value (name)
  // For Arcade/GameRoom types, use existing postingLocation or fallback to name
  return calculatedValue || data?.postingLocation || name || '';
};

/**
 * Calculates the postingLocation value for a sublocation
 * @param {Object} subLocationData - Object containing sublocation data with properties:
 *   - postingLocation: existing postingLocation value (optional)
 *   - productType or productCategories: the product type object or array of product categories
 *   - subLocationName or name: the sublocation name
 * @param {Array} productCategoriesData - Array of product category objects with id and name properties
 * @param {boolean} preserveExisting - If true, preserves existing postingLocation if present (default: true)
 * @returns {string} - The calculated postingLocation value
 */
export const getPostingLocationValue = (subLocationData, productCategoriesData, preserveExisting = true) => {
  // If there's an existing postingLocation from backend and we want to preserve it, use it
  if (preserveExisting && subLocationData?.postingLocation) {
    return subLocationData.postingLocation;
  }

  // Extract product type - could be productType object or productCategories array
  const productType = subLocationData?.productType || subLocationData?.productCategories;
  const name = extractName(subLocationData);
  return calculatePostingLocationFromTypeAndName(productType, name, productCategoriesData);
};
